import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  MapPin, 
  ArrowUpRight,
  Filter,
  X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { id: 'all', label: 'Tutti' },
  { id: 'linee_vita', label: 'Linee Vita' },
  { id: 'parapetti', label: 'Parapetti' },
  { id: 'revisione', label: 'Revisioni' }
];

// Fallback projects in case API is empty
const fallbackProjects = [
  {
    id: '1',
    title: 'Centro Commerciale Verona Est',
    description: 'Installazione di sistema linea vita tipo C su copertura metallica di 3.500 m².',
    location: 'Verona, VR',
    category: 'linee_vita',
    images: ['https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800'],
    featured: true
  },
  {
    id: '2',
    title: 'Condominio Residenze del Parco',
    description: 'Progettazione e installazione linee vita per manutenzione coperture condominiali.',
    location: 'Brescia, BS',
    category: 'linee_vita',
    images: ['https://images.unsplash.com/photo-1632759888494-0d33e50625f5?w=800'],
    featured: true
  },
  {
    id: '3',
    title: 'Stabilimento Industriale ZAI',
    description: 'Sistema parapetti modulari per protezione perimetrale su capannone industriale.',
    location: 'Verona, VR',
    category: 'parapetti',
    images: ['https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800'],
    featured: true
  },
  {
    id: '4',
    title: 'Villa Privata Lago di Garda',
    description: 'Revisione e certificazione sistema anticaduta esistente su villa unifamiliare.',
    location: 'Peschiera del Garda, VR',
    category: 'revisione',
    images: ['https://images.unsplash.com/photo-1581094794329-cd675335442f?w=800'],
    featured: false
  },
  {
    id: '5',
    title: 'Hotel Riviera',
    description: 'Installazione completa sistema anticaduta su tetto piano con aree tecniche.',
    location: 'Riva del Garda, TN',
    category: 'linee_vita',
    images: ['https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800'],
    featured: false
  },
  {
    id: '6',
    title: 'Capannone Logistico Nord',
    description: 'Parapetti perimetrali e linee vita per accesso a pannelli fotovoltaici.',
    location: 'Mantova, MN',
    category: 'parapetti',
    images: ['https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800'],
    featured: false
  }
];

const GalleryPage = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.category === activeCategory));
    }
  }, [activeCategory, projects]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      if (response.data && response.data.length > 0) {
        setProjects(response.data);
        setFilteredProjects(response.data);
      } else {
        setProjects(fallbackProjects);
        setFilteredProjects(fallbackProjects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects(fallbackProjects);
      setFilteredProjects(fallbackProjects);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.id === category);
    return cat ? cat.label : category;
  };

  return (
    <div className="bg-revo-bg pt-20 min-h-screen">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
              I Nostri Progetti
            </span>
            <h1 className="font-barlow text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white mt-4 mb-6">
              Gallery<br />
              <span className="text-gray-500">Realizzazioni</span>
            </h1>
            <p className="font-manrope text-lg text-gray-300 leading-relaxed">
              Scopri i nostri progetti di installazione e manutenzione di sistemi anticaduta 
              su edifici industriali, civili e storici.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-revo-paper border-y border-revo-border sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex items-center gap-4 overflow-x-auto pb-2">
            <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`filter-${cat.id}`}
                className={`px-4 py-2 font-barlow text-sm uppercase tracking-wider whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? 'bg-revo-orange text-white'
                    : 'bg-revo-bg border border-revo-border text-gray-400 hover:border-revo-orange hover:text-white'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/3] bg-revo-paper animate-pulse" />
              ))}
            </div>
          ) : (
            <motion.div 
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="gallery-grid"
            >
              {filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  onClick={() => setSelectedProject(project)}
                  className="group cursor-pointer"
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden border border-revo-border bg-revo-paper">
                    <img 
                      src={project.images[0]} 
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-revo-bg/90 via-revo-bg/20 to-transparent" />
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-revo-bg/80 backdrop-blur-sm border border-revo-border text-xs font-manrope text-gray-300 uppercase tracking-wider">
                        {getCategoryLabel(project.category)}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="font-barlow text-xl font-bold uppercase tracking-wide text-white mb-2 group-hover:text-revo-orange transition-colors">
                        {project.title}
                      </h3>
                      <div className="flex items-center gap-2 text-gray-400">
                        <MapPin className="w-4 h-4" />
                        <span className="font-manrope text-sm">{project.location}</span>
                      </div>
                    </div>

                    {/* Hover Arrow */}
                    <div className="absolute top-4 right-4 w-10 h-10 bg-revo-orange flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowUpRight className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {filteredProjects.length === 0 && !loading && (
            <div className="text-center py-16">
              <p className="font-manrope text-gray-500">
                Nessun progetto trovato per questa categoria.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-revo-bg/90 backdrop-blur-sm"
          onClick={() => setSelectedProject(null)}
          data-testid="project-modal"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative max-w-4xl w-full bg-revo-paper border border-revo-border"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedProject(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-revo-bg border border-revo-border flex items-center justify-center text-white hover:border-revo-orange transition-colors"
              data-testid="modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="aspect-video overflow-hidden">
              <img 
                src={selectedProject.images[0]} 
                alt={selectedProject.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="p-8">
              <span className="px-3 py-1 bg-revo-orange/10 border border-revo-orange/20 text-xs font-manrope text-revo-orange uppercase tracking-wider">
                {getCategoryLabel(selectedProject.category)}
              </span>
              <h2 className="font-barlow text-3xl font-bold uppercase tracking-tight text-white mt-4 mb-2">
                {selectedProject.title}
              </h2>
              <div className="flex items-center gap-2 text-gray-400 mb-6">
                <MapPin className="w-4 h-4" />
                <span className="font-manrope">{selectedProject.location}</span>
              </div>
              <p className="font-manrope text-gray-400 leading-relaxed">
                {selectedProject.description}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default GalleryPage;
